import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { ExcoVideo } from 'site-modules/shared/components/exco-video/exco-video';
import { getTargeting, subscribeTargeting } from 'site-modules/shared/components/ads-context/ads-context';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';

const EXCO_ID = 'dd4811f8-d71b-4167-8f77-20be06c87b33'; // placeholder demo id: '073e5784-8789-45e0-a132-61b9999bc9af';
const EMPTY_TARGETING = {};

export function getTargetingSnapshot() {
  return getTargeting();
}

export function getServerTargetingSnapshot() {
  return EMPTY_TARGETING;
}

export function ExcoVideoWrapperUI({
  vehicle = null,
  isMobile = false,
  pageCategory = null,
  pageName = null,
  sessionId = null,
  visitorId = null,
  zipCode = null,
  doNotSell = false,
  uspString = null,
  sect1 = null,
  sect2 = null,
  showTestText = true,
  classes = null,
  styles = null,
  playVideoPlaylist = null,
  playerReadyCallback = null,
}) {
  const targeting = useSyncExternalStore(subscribeTargeting, getTargetingSnapshot, getServerTargetingSnapshot);
  const excoPropsFromMapStateToProps = {
    mk: get(vehicle, 'make.niceName', ''),
    mdl: get(vehicle, 'model.niceName', ''),
    sub: get(vehicle, 'subModel.niceName', ''),
    year: get(vehicle, 'modelYear.year', ''),
    type: get(vehicle, 'type.niceName', ''),
    mobile: isMobile,
    edwcat: pageCategory,
    edwpg: pageName,
    edw: sessionId,
    vid: visitorId,
    zip: zipCode,
    doNotSell,
    usPrivacy: uspString,
    sect1,
    sect2,
  };

  const timeoutRef = useRef(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [forceInit, setForceInit] = useState(false);

  const isTargetingReady = !!targeting?.fr;

  const onEnteredView = useCallback(() => {
    setHasEnteredView(true);
  }, []);

  useEffect(() => {
    if (!hasEnteredView || isTargetingReady || forceInit) {
      return () => {};
    }

    timeoutRef.current = setTimeout(() => setForceInit(true), 10000);

    return () => clearTimeout(timeoutRef.current);
  }, [forceInit, hasEnteredView, isTargetingReady]);

  return (
    <RenderWhenViewable verticalOffset="500%" onRender={onEnteredView}>
      {(isTargetingReady || forceInit) && (
        <ExcoVideo
          id={EXCO_ID}
          excoProps={excoPropsFromMapStateToProps}
          customTargeting={targeting}
          showTestText={showTestText}
          classes={classes}
          styles={styles}
          data-tracking-id="exco_video_player"
          playVideoPlaylist={playVideoPlaylist}
          playerReadyCallback={playerReadyCallback}
        />
      )}
    </RenderWhenViewable>
  );
}
ExcoVideoWrapperUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  isMobile: PropTypes.bool,
  pageName: PropTypes.string,
  pageCategory: PropTypes.string,
  zipCode: PropTypes.string,
  visitorId: PropTypes.string,
  sessionId: PropTypes.string,
  doNotSell: PropTypes.bool,
  uspString: PropTypes.string,
  sect1: PropTypes.string,
  sect2: PropTypes.string,
  showTestText: PropTypes.bool,
  classes: PropTypes.string,
  styles: PropTypes.shape({}),
  playVideoPlaylist: PropTypes.func,
  playerReadyCallback: PropTypes.func,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  pageName: state.pageContext?.page?.name,
  pageCategory: state.pageContext?.page?.category,
  zipCode: state.visitor?.location?.zipCode,
  visitorId: state.visitor?.id,
  sessionId: state.visitor?.session,
  doNotSell: state.visitor?.privacy?.doNotSell,
  uspString: state.visitor?.privacy?.uspString,
  vehicle: state.pageContext?.vehicle,
  sect1: state.pageContext?.page?.adsCustomTargeting?.sect1,
  sect2: state.pageContext?.page?.adsCustomTargeting?.sect2,
});

export const ExcoVideoWrapper = connect(mapStateToProps)(ExcoVideoWrapperUI);
